import { badgeColours, initials } from "../lib/franchise-badge.js";
import type { Franchise } from "../lib/types.js";

/**
 * A franchise's mark: its logo contained in a square, or its initials on its colour.
 *
 * The slot is the same size either way, so a row with a logo and one without line up. The
 * logo sits on the ground colour with a hairline, as a tournament's does, so a dark or
 * transparent mark reads on any theme.
 */
export function FranchiseBadge({
  franchise,
  size = 44,
}: {
  franchise: Pick<Franchise, "name" | "shortName" | "logoUrl" | "colour">;
  size?: number;
}) {
  const { background, text } = badgeColours(franchise.colour);

  return (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden"
      style={{
        width: size,
        height: size,
        ...(franchise.logoUrl
          ? { background: "var(--color-ground)", border: "1px solid var(--color-line-muted)" }
          : { background, color: text }),
      }}
      data-testid="franchise-badge"
    >
      {franchise.logoUrl ? (
        <img
          src={franchise.logoUrl}
          alt={`${franchise.name} logo`}
          className="size-full object-contain"
        />
      ) : (
        <span
          aria-hidden
          className="font-display uppercase"
          style={{ fontSize: size * 0.36, fontWeight: 800, letterSpacing: "0.02em" }}
        >
          {initials(franchise)}
        </span>
      )}
    </span>
  );
}
